/**
 * src/features/admin/ui/SkeletonCards.tsx
 *
 * Loading skeleton for the admin metrics grid (ADMIN-01).
 * Matches the real grid layout to prevent layout shift.
 * Extracted from AdminMetricsPage to keep page component under 200 lines (CLAUDE.md rule 5).
 */

import React from 'react';
import { Card, CardContent } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/Skeleton';

export function SkeletonCards(): React.ReactElement {
  return (
    <div className="space-y-8">
      {/* Count grid skeleton */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {[0, 1, 2, 3].map((i) => (
          <Card key={i} aria-hidden="true">
            <CardContent className="p-6 space-y-3">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-7 w-28" />
            </CardContent>
          </Card>
        ))}
      </div>
      {/* MRR skeleton */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
        {[0, 1].map((i) => (
          <Card key={i} aria-hidden="true">
            <CardContent className="p-6 space-y-3">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-7 w-32" />
              <Skeleton className="h-3 w-36" />
            </CardContent>
          </Card>
        ))}
      </div>
      {/* Activation rate grid skeleton (8 cards, matches ActivationRateGrid layout) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <Card key={i} aria-hidden="true">
            <CardContent className="p-6 space-y-3">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-7 w-20" />
              <Skeleton className="h-3 w-24" />
            </CardContent>
          </Card>
        ))}
      </div>
      {/* Time-to-export skeleton (2 cards, matches TimeToExportCards) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
        {[0, 1].map((i) => (
          <Card key={i} aria-hidden="true">
            <CardContent className="p-6 space-y-3">
              <Skeleton className="h-3 w-28" />
              <Skeleton className="h-7 w-20" />
              <Skeleton className="h-3 w-32" />
            </CardContent>
          </Card>
        ))}
      </div>
      {/* CohortTable skeleton (full-width card with table rows) */}
      <Card aria-hidden="true">
        <CardContent className="p-6 space-y-3">
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
        </CardContent>
      </Card>
    </div>
  );
}
